import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Skeleton,
  useMediaQuery,
  useTheme,
  Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ChatIcon from '@mui/icons-material/Chat';
import BugReportIcon from '@mui/icons-material/BugReport';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import PushPinIcon from '@mui/icons-material/PushPin';
import SEO from '../components/SEO';

const HOT_THRESHOLD = 20;

const TopicCard = ({ topic, index, isMobile }) => {
  const isFeedback = topic.icon === 'feedback';
  const isHot = topic.posts >= HOT_THRESHOLD;

  return (
    <Box component="li" key={topic.topic_id || index} sx={{ mb: 2 }}>
      <Card
        component={Link}
        to={`/forum/${topic.topic_id}`}
        sx={{
          display: 'block',
          textDecoration: 'none',
          backgroundColor: isFeedback
            ? 'rgba(255, 193, 7, 0.12)'
            : 'rgba(255, 255, 255, 0.08)',
          color: '#ffffff',
          border: isFeedback
            ? '1px solid rgba(255, 193, 7, 0.35)'
            : '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: 2,
          transition: 'transform 0.2s, box-shadow 0.2s, background-color 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: isFeedback
              ? '0 6px 20px rgba(255, 193, 7, 0.2)'
              : '0 6px 20px rgba(0, 0, 0, 0.3)',
            backgroundColor: isFeedback
              ? 'rgba(255, 193, 7, 0.18)'
              : 'rgba(255, 255, 255, 0.13)'
          },
          '&:focus-visible': {
            outline: '2px solid #24CC9F',
            outlineOffset: 2
          }
        }}
        aria-label={`Go to topic: ${topic.title}`}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            p: { xs: 2, sm: 2.5 },
            gap: { xs: 1.5, sm: 0 },
            '&:last-child': { pb: { xs: 2, sm: 2.5 } }
          }}
        >
          {/* Icon + title + description */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: isFeedback ? 'rgba(255, 193, 7, 0.25)' : 'rgba(255, 255, 255, 0.15)'
              }}
            >
              {isFeedback
                ? <BugReportIcon sx={{ color: '#FFC107', fontSize: { xs: 22, sm: 26 } }} aria-hidden />
                : <ChatIcon sx={{ color: '#ffffff', fontSize: { xs: 22, sm: 26 } }} aria-hidden />
              }
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography
                  variant={isMobile ? 'subtitle1' : 'h6'}
                  component="h2"
                  sx={{ fontWeight: 600, color: '#ffffff', wordBreak: 'break-word' }}
                >
                  {topic.title}
                </Typography>
                {isFeedback && (
                  <Chip
                    icon={<PushPinIcon sx={{ fontSize: '14px !important' }} />}
                    label="Pinned"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: 'rgba(255, 193, 7, 0.2)',
                      color: '#FFC107',
                      border: '1px solid rgba(255, 193, 7, 0.4)',
                      '.MuiChip-icon': { color: '#FFC107' }
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {topic.description}
              </Typography>
            </Box>
          </Box>

          {/* Stats */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 3, sm: 4 },
              mt: { xs: 0.5, sm: 0 },
              ml: { xs: 7, sm: 3 },
              flexShrink: 0
            }}
          >
            <Box sx={{ textAlign: 'center', minWidth: 48 }}>
              <Typography
                variant="h6"
                sx={{ color: '#ffffff', fontWeight: 700, lineHeight: 1.2 }}
                aria-label={`${topic.posts} posts`}
              >
                {topic.posts}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>
                posts
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', minWidth: 70 }}>
              {topic.posts > 0 ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{ color: '#ffffff', fontWeight: 500, lineHeight: 1.2 }}
                    aria-label={`Last active ${topic.lastActive}`}
                  >
                    {topic.lastActive}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>
                    last post
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', fontStyle: 'italic' }}>
                  No posts yet
                </Typography>
              )}
            </Box>

            <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
              {isHot && (
                <LocalFireDepartmentIcon
                  sx={{ color: '#FF5722', fontSize: 22 }}
                  aria-label="Hot topic"
                  titleAccess="Hot topic"
                />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const SectionHeader = ({ title, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
    <Typography
      variant="overline"
      sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 700, letterSpacing: 1.5, fontSize: '0.7rem' }}
    >
      {title}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.1)' }} />
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}>
      {count} {count === 1 ? 'topic' : 'topics'}
    </Typography>
  </Box>
);

const SkeletonCard = ({ isMobile }) => (
  <Skeleton
    variant="rectangular"
    height={isMobile ? 100 : 90}
    animation="wave"
    sx={{ bgcolor: 'rgba(255, 255, 255, 0.08)', borderRadius: 2, mb: 2 }}
  />
);

const ForumPage = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/forum/topics');
        setTopics(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || 'Error loading forum topics');
        console.error('Error fetching topics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTopics();
  }, []);

  const feedbackTopics = topics.filter(t => t.icon === 'feedback');
  const communityTopics = topics.filter(t => t.icon !== 'feedback');

  return (
    <Box
      component="main"
      role="main"
      aria-label="Forum topics"
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: '1100px',
        mx: 'auto',
        backgroundColor: '#221FC7',
        borderRadius: 2,
        color: '#ffffff',
        minHeight: '80vh'
      }}
    >
      <SEO
        title="Community Forum"
        description="Join the Trainer Academy community. Discuss VGC strategies, share replays, ask questions, and report bugs."
        keywords="pokemon forum, vgc community, pokemon discussion, competitive pokemon community, vgc strategies"
      />

      <Typography
        variant={isMobile ? 'h5' : 'h4'}
        component="h1"
        sx={{ fontWeight: 700, color: '#ffffff', mb: 0.5 }}
      >
        Forum
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mb: 3 }}>
        Discussions, strategies and community feedback
      </Typography>

      <Divider sx={{ mb: 3, bgcolor: 'rgba(255, 255, 255, 0.12)' }} />

      {loading ? (
        <Box>
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} isMobile={isMobile} />)}
        </Box>
      ) : error ? (
        <Typography
          variant="body1"
          sx={{ p: 2, bgcolor: 'rgba(255, 0, 0, 0.1)', borderRadius: 1, color: '#ff6b6b' }}
          role="alert"
        >
          {error}
        </Typography>
      ) : topics.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ChatIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            No topics yet. Be the first to start a discussion!
          </Typography>
        </Box>
      ) : (
        <Box>
          {feedbackTopics.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionHeader title="Feedback & Development" count={feedbackTopics.length} />
              <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                {feedbackTopics.map((topic, index) => (
                  <TopicCard key={topic.topic_id} topic={topic} index={index} isMobile={isMobile} />
                ))}
              </Box>
            </Box>
          )}

          {communityTopics.length > 0 && (
            <Box>
              <SectionHeader title="Community" count={communityTopics.length} />
              <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                {communityTopics.map((topic, index) => (
                  <TopicCard key={topic.topic_id} topic={topic} index={index} isMobile={isMobile} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ForumPage;
